"use client";

import { useMediaQuery } from "@dub/ui";
import { Search } from "lucide-react";
import { motion } from "motion/react";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useMemo,
  useState,
} from "react";
import { cn } from "../../../utils";

// Simple utility to format URLs nicely (replaces @dub/utils getPrettyUrl)
function getPrettyUrl(url: string): string {
  if (!url) return url;
  return url
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "");
}

export function BarList({
  tab,
  data,
  barBackground,
  hoverBackground,
  setShowModal,
  limit,
}: {
  tab: string;
  unit?: string;
  data: {
    icon: ReactNode;
    title: string;
    href?: string;
    value: number;
    linkId?: string;
    itemBarBackground?: string;
  }[];
  maxValue?: number;
  barBackground: string;
  hoverBackground: string;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  limit?: number;
}) {
  const [search, setSearch] = useState("");

  // Calculate total sum for percentage calculations
  const totalSum = useMemo(
    () => data.reduce((sum, item) => sum + item.value, 0),
    [data],
  );

  const filteredData = useMemo(() => {
    if (limit) {
      return data.slice(0, limit);
    } else {
      return search
        ? data.filter((d) =>
            d.title.toLowerCase().includes(search.toLowerCase()),
          )
        : data;
    }
  }, [data, limit, search]);

  const { isMobile } = useMediaQuery();

  const bars = (
    <div className="relative grid h-full auto-rows-min grid-cols-1">
      {filteredData.map((item, idx) => (
        <LineItem
          key={idx}
          icon={item.icon}
          title={item.title}
          href={item.href}
          value={item.value}
          totalSum={totalSum}
          setShowModal={setShowModal}
          barBackground={barBackground}
          hoverBackground={hoverBackground}
          itemBarBackground={item.itemBarBackground}
          limit={limit}
        />
      ))}
    </div>
  );

  if (limit) {
    return bars;
  } else {
    return (
      <>
        <div className="relative px-4 py-3">
          <div className="pointer-events-none absolute inset-y-0 left-7 flex items-center">
            <Search className="h-4 w-4 text-neutral-400" />
          </div>
          <input
            type="text"
            autoFocus={!isMobile}
            className="w-full rounded-md border border-neutral-300 py-2 pl-10 text-black placeholder:text-neutral-400 focus:border-neutral-500 focus:outline-none focus:ring-4 focus:ring-neutral-200 sm:text-sm"
            placeholder={`Search ${tab}...`}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="h-[50vh] overflow-auto pb-4 md:h-[40vh]">{bars}</div>
      </>
    );
  }
}

export function LineItem({
  icon,
  title,
  href,
  value,
  totalSum,
  setShowModal,
  barBackground,
  hoverBackground,
  limit,
  itemBarBackground,
}: {
  icon: ReactNode;
  title: string;
  href?: string;
  value: number;
  totalSum: number;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  barBackground: string;
  hoverBackground: string;
  limit?: number;
  itemBarBackground?: string;
}) {
  const lineItem = useMemo(() => {
    return (
      <div className="z-10 flex items-center space-x-4 overflow-hidden px-3">
        {icon}
        <div className="truncate text-sm text-neutral-800">
          {getPrettyUrl(title)}
        </div>
      </div>
    );
  }, [icon, title]);

  // Calculate percentage against total sum and round to 1 decimal
  const percentage = totalSum > 0 ? Math.round((value / totalSum) * 1000) / 10 : 0;

  // Check if we're in modal view - if limit is undefined, we're in the modal view
  const isModalView = !limit;

  const formattedValue = useMemo(() => {
    return Intl.NumberFormat("en-US", {
      notation: value > 999999 ? "compact" : "standard",
    }).format(value);
  }, [value]);

  const content = (
    <div
      className={cn(
        `block min-w-0 border-l-2 border-transparent px-4 py-1 transition-all`,
        href && hoverBackground,
        isModalView ? "group" : "",
      )}
      onClick={() => {
        if (href) setShowModal(false);
      }}
    >
      <div
        className={cn(
          "relative flex items-center justify-between",
          isModalView && "gap-16",
        )}
      >
        <motion.div
          style={{
            width: `${percentage}%`,
            position: "absolute",
            inset: 0,
          }}
          className={cn("-z-10 h-full origin-left rounded-md", itemBarBackground || barBackground)}
          transition={{ ease: "easeOut", duration: 0.3 }}
          initial={{ transform: "scaleX(0)" }}
          animate={{ transform: "scaleX(1)" }}
        />
        <div className="relative z-10 flex h-8 w-full min-w-0 max-w-[calc(100%-2rem)] items-center transition-[max-width] duration-300 ease-in-out group-hover:max-w-[calc(100%-5rem)]">
          {lineItem}
        </div>
        <div className="z-10 flex items-center">
          <span
            className={cn(
              "z-10 px-2 text-sm text-neutral-600 transition-transform duration-300",
              isModalView ? "-translate-x-14" : "group-hover:-translate-x-14",
            )}
            style={{
              transform: `translateX(var(--tw-translate-x, 0)) translateZ(0)`,
            }}
          >
            {formattedValue}
          </span>
          <div
            className={cn(
              "absolute right-0 px-3 text-sm text-neutral-600/70 transition-all duration-300",
              isModalView
                ? "visible translate-x-0 opacity-100"
                : "invisible translate-x-14 opacity-0 group-hover:visible group-hover:translate-x-0 group-hover:opacity-100",
            )}
            style={{
              transform: `translateX(var(--tw-translate-x, 0)) translateZ(0)`,
            }}
          >
            {percentage}%
          </div>
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <a href={href} onClick={() => setShowModal(false)}>
        {content}
      </a>
    );
  }

  return content;
}
